import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import ContactSalesModal from '../components/ContactSalesModal';
import '../styles/PricingPage.css';

const PricingPage = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(true);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [openFaqItems, setOpenFaqItems] = useState({});
  const [expandedFaq, setExpandedFaq] = useState(false);

  const handleGetStarted = (isFounderDeal = false) => {
    if (isFounderDeal) {
      navigate('/signup?founder_deal=true');
    } else {
      navigate('/signup');
    }
  };

  const handleContactSales = () => {
    setContactModalOpen(true);
  };

  const toggleFaqItem = (index) => {
    setOpenFaqItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const founderFaqs = [
    {
      question: "Do I need to upgrade to get the Shluchim Deal?",
      answer: "No. Activation on the FREE plan is enough. You pay nothing now."
    },
    {
      question: "What counts as activation?",
      answer: "Getting your first 25 members to join your Yesod community space."
    },
    {
      question: "What if I don't upgrade later?",
      answer: "You keep using the Free plan forever. Premium is optional."
    },
    {
      question: "Will the price increase later?",
      answer: "For the Shluchim Deal, the discounted rate is locked in for life."
    }
  ];

  return (
    <div className="pricing-page">
      <div className="pricing-container">
        {/* Header */}
        <header className="pricing-header">
          <div className="logo-container">
            <img 
              src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Yesod%20Logo%204K.png" 
              alt="Yesod Logo" 
              className="pricing-logo"
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <nav className="pricing-nav">
            <button className="nav-link" onClick={() => navigate('/')}>Home</button>
            <button className="nav-link active">Pricing</button>
            <button className="nav-link" onClick={() => navigate('/login')}>Log in</button>
            <button className="nav-btn-signup" onClick={() => navigate('/signup')}>Sign Up</button>
          </nav>
        </header>

        {/* Hero Section */}
        <motion.div 
          className="pricing-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Typography variant="h1" className="pricing-hero-title">
            The best community solution,<br />for the best price.
          </Typography>
        </motion.div>

        {/* Billing Toggle */}
        <div className="billing-toggle-container">
          <span className="toggle-savings">Save up to 20% with yearly</span>
          <div className="billing-toggle">
            <button 
              className={`toggle-option ${!isYearly ? 'active' : ''}`}
              onClick={() => setIsYearly(false)}
            >
              Monthly
            </button>
            <button 
              className={`toggle-option ${isYearly ? 'active' : ''}`}
              onClick={() => setIsYearly(true)}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="pricing-cards-container">
          {/* Free Forever */}
          <motion.div 
            className="pricing-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="pricing-card-header">
              <Typography variant="h3" className="pricing-card-title">
                Free Forever
              </Typography>
              <div className="pricing-card-price">
                <span className="price-currency">$</span>
                <span className="price-amount">0</span>
                <span className="price-period">/month</span>
              </div>
              <div className="pricing-card-fees">
                <p>Stripe fees for credit card processing</p>
                <p>1% service fee</p>
              </div>
            </div>
            
            <Button 
              variant="contained" 
              className="pricing-cta pricing-cta-blue"
              onClick={handleGetStarted}
            >
              Get Started
            </Button>

            <div className="pricing-features">
              <Typography variant="subtitle2" className="features-label">
                KEY FEATURES:
              </Typography>
              <ul className="features-list">
                <li>Free community app</li>
                <li>Unlimited members</li>
                <li>Unlimited posts</li>
                <li>Unlimited Events</li>
                <li>Unlimited Classes</li>
                <li>Limited Yesod AI</li>
                <li>Limited push notifications</li>
              </ul>
            </div>
          </motion.div>

          {/* Premium */}
          <motion.div 
            className="pricing-card pricing-card-popular"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="pricing-card-header">
              <div className="popular-badge">Coming Soon</div>
              <Typography variant="h3" className="pricing-card-title">
                Premium
              </Typography>
              <div className="pricing-card-price">
                <span className="price-currency">$</span>
                <span className="price-amount">{isYearly ? '80' : '99'}</span>
                <span className="price-period">/month{isYearly ? ', paid yearly' : ''}</span>
              </div>
              <div className="pricing-card-fees">
                <p>Stripe fees for credit card processing</p>
                <p>0.5% service fee</p>
              </div>
            </div>
            
            <Button 
              variant="contained" 
              className="pricing-cta pricing-cta-light"
              onClick={handleContactSales}
            >
              Book a Demo
            </Button>

            <div className="pricing-features">
              <Typography variant="subtitle2" className="features-label">
                EVERYTHING IN FREE +
              </Typography>
              <ul className="features-list">
                <li>Unlimited Yesod AI</li>
                <li>Unlimited push notifications</li>
                <li>Salesforce integration</li>
                <li>Feature requests</li>
                <li>24/6 support</li>
              </ul>
            </div>
          </motion.div>

          {/* Enterprise */}
          <motion.div 
            className="pricing-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="pricing-card-header">
              <Typography variant="h3" className="pricing-card-title">
                Enterprise
              </Typography>
              <div className="pricing-card-price">
                <span className="price-amount">Flexible pricing</span>
              </div>
              <div className="pricing-card-fees">
                <p>Customized volume-based processing fees</p>
              </div>
            </div>
            
            <Button 
              variant="contained" 
              className="pricing-cta pricing-cta-dark"
              onClick={handleContactSales}
            >
              Book a Demo
            </Button>

            <div className="pricing-features">
              <Typography variant="subtitle2" className="features-label">
                EVERYTHING IN PREMIUM +
              </Typography>
              <ul className="features-list">
                <li>Advanced analytics</li>
                <li>Customized workflows</li>
                <li>Custom integrations</li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Push Notifications Section */}
        <motion.div 
          className="push-notifications-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Typography variant="h2" className="push-section-main-title">
            Stop Losing Members in the Chaos
          </Typography>
          <Typography variant="body1" className="push-section-subtitle">
            Most communities are scattered across too many apps.
          </Typography>

          <div className="comparison-container">
            {/* Left Side - Current Fragmented Approach */}
            <div className="fragmented-tools">
              <div className="tools-header">
                <Typography variant="h3" className="comparison-title">
                  What your members go through
                </Typography>
                <Typography variant="body2" className="comparison-subtitle">
                  Just to donate, RSVP, or check minyan times
                </Typography>
              </div>
              
              {/* Messy visualization with icons and lines */}
              <div className="messy-tools-visual">
                <svg className="connection-lines" viewBox="0 0 500 350" preserveAspectRatio="none">
                  {/* Multiple tangled curved paths */}
                  <path d="M50,50 Q150,100 250,80 T450,120" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.5"/>
                  <path d="M100,40 Q200,120 300,100 T480,90" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  <path d="M30,100 Q120,60 220,140 T420,110" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.45"/>
                  <path d="M80,150 Q180,80 280,160 T460,140" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.4"/>
                  <path d="M40,180 Q140,220 240,180 T440,200" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.5"/>
                  <path d="M90,200 Q190,150 290,220 T470,180" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  <path d="M60,240 Q160,190 260,250 T450,230" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.45"/>
                  <path d="M110,260 Q210,280 310,240 T480,270" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.4"/>
                  <path d="M20,280 Q130,250 230,300 T430,290" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.5"/>
                  <path d="M70,310 Q170,330 270,290 T460,320" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  {/* Additional criss-cross paths */}
                  <path d="M480,60 Q380,140 280,100 T80,160" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.35"/>
                  <path d="M450,110 Q350,70 250,150 T50,120" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  <path d="M470,180 Q370,220 270,170 T70,240" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.35"/>
                  <path d="M460,250 Q360,190 260,260 T60,220" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                </svg>
                
                <div className="tools-grid">
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <span className="tool-name">WhatsApp</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#EA4335">
                        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Gmail</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#4285F4">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Google Forms</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#0F9D58">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Sheets</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#6D1ED4">
                        <path d="M20.625 8.127c-2.05 0-3.719 1.67-3.719 3.719 0 2.05 1.669 3.719 3.719 3.719 2.05 0 3.719-1.67 3.719-3.719 0-2.05-1.669-3.719-3.719-3.719zm0 6.375c-1.467 0-2.656-1.19-2.656-2.656s1.189-2.656 2.656-2.656c1.467 0 2.656 1.19 2.656 2.656s-1.189 2.656-2.656 2.656zM3.375 8.127C1.325 8.127-.344 9.796-.344 11.846c0 2.05 1.669 3.719 3.719 3.719 2.05 0 3.719-1.67 3.719-3.719 0-2.05-1.669-3.719-3.719-3.719zm0 6.375c-1.467 0-2.656-1.19-2.656-2.656S1.908 9.19 3.375 9.19c1.467 0 2.656 1.19 2.656 2.656s-1.189 2.656-2.656 2.656z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Zelle</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#003087">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.76-4.852a.931.931 0 0 1 .922-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.720-4.589z"/>
                      </svg>
                    </div>
                    <span className="tool-name">PayPal</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#5f6368">
                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10zm-8-7c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        <circle cx="12" cy="14" r="2"/>
                        <path d="M4 6h16v2H4z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Your Website</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#3b82f6">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Member Portal</span>
                  </div>
                </div>
                
                {/* Question bubbles - positioned around the edges */}
                <div className="question-bubble bubble-1">What time is Mincha?</div>
                <div className="question-bubble bubble-2">Can someone share the donation link?</div>
                <div className="question-bubble bubble-3">Where's the RSVP form?</div>
                <div className="question-bubble bubble-4">Which WhatsApp group is it in?</div>
                <div className="question-bubble bubble-5">Is this the right email?</div>
              </div>
              
              <div className="tools-issue-label">
                Messages get buried → Links get lost → Members disengage
              </div>
            </div>

            {/* Arrow */}
            <div className="arrow-divider">
              <svg className="arrow-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>

            {/* Right Side - Yesod Solution */}
            <div className="yesod-solution">
              <div className="solution-header">
                <Typography variant="h3" className="comparison-title">
                  With Yesod
                </Typography>
                <div className="solution-subtitle">Push notifications deliver 8× more conversions than email</div>
              </div>
              
              {/* Stacked Notifications */}
              <div className="stacked-notifications">
                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">Your pledge is ready to complete. Tap to finish.</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • now</div>
                  </div>
                </div>

                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">Upcoming: Chanukah Party 2025! Tap to RSVP</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • 5m ago</div>
                  </div>
                </div>

                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">Mincha starting in 10 minutes</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • 15m ago</div>
                  </div>
                </div>

                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">This week's newsletter</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • 3h ago</div>
                  </div>
                </div>
              </div>

              <div className="tools-issue-label" style={{ marginTop: '24px', fontSize: '0.8rem' }}>
                Instant notifications → One tap to action → Instant engagement
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="push-cta-container">
            <Button 
              variant="contained" 
              size="large"
              className="push-cta-button"
              onClick={() => navigate('/signup')}
            >
              Get Started for Free
            </Button>
          </div>
        </motion.div>

        {/* Contact Sales Modal */}
        <ContactSalesModal 
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default PricingPage;
